// The monthly web sweep's progress (milestone: web crawl). The sweep runs
// as its own Jobs, not pipeline runs, so it has its own view.
import { useQuery } from '@tanstack/react-query'

import { requestRaw } from '../api'
import { ErrorBox, Pill } from '../components'
import { dt } from '../format'

interface Sweep {
  sweep: string
  shards: number
  done: number
  in_progress: number
  workers: number
  domains_done: number
  started_at: string | null
  finished_at: string | null
  robots: number
  files: number
  dns: number
  discovery: number
}

const num = (n: number) => n.toLocaleString('sv-SE').replace(/ /g, ' ')

export function Crawl() {
  const q = useQuery({
    queryKey: ['crawl-sweeps'],
    queryFn: async () => (await requestRaw('GET', '/crawl/sweeps')).json() as Promise<Sweep[]>,
    refetchInterval: 15_000,
  })
  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Web crawl</h1>
          <p className="sub">
            The monthly sweep of every .se and .nu domain for its public robots.txt, agent files and DNS. It runs as its own Jobs
            (not pipeline runs); this reads the shard progress. Datasets: the <code>web-crawl</code> source and the{' '}
            <code>web_*</code> buckets.
          </p>
        </div>
      </header>
      {q.error && <ErrorBox error={q.error} />}
      {q.data?.length === 0 && <p className="muted">No sweeps yet. Start one from the cluster (the aleva-crawl CronJob).</p>}
      <div className="changes">
        {(q.data ?? []).map((s) => {
          const pct = s.shards ? Math.round((100 * s.done) / s.shards) : 0
          const running = s.in_progress > 0 || (!s.finished_at && s.done < s.shards)
          return (
            <div key={s.sweep} className="card">
              <div className="row-between">
                <h2>
                  {s.sweep}{' '}
                  {s.finished_at ? <Pill kind="ok">complete</Pill> : running ? <Pill kind="info">running · {s.workers} pods</Pill> : <Pill kind="neutral">idle</Pill>}
                </h2>
                <span className="muted">
                  {s.done} / {s.shards} shards ({pct}%){s.in_progress > 0 ? ` · ${s.in_progress} in progress` : ''}
                </span>
              </div>
              <div className="crawl-bar" aria-label={`${pct}% of shards done`}>
                <span style={{ width: `${pct}%` }} />
              </div>
              <dl className="kv">
                <dt>Domains crawled</dt>
                <dd>{num(s.domains_done)}</dd>
                <dt>robots.txt rows</dt>
                <dd>{num(s.robots)}</dd>
                <dt>Public files found</dt>
                <dd>{num(s.files)}</dd>
                <dt>DNS rows</dt>
                <dd>{num(s.dns)}</dd>
                <dt>Discovered endpoints</dt>
                <dd>{num(s.discovery)}</dd>
                <dt>Started</dt>
                <dd>{s.started_at ? dt(s.started_at) : '–'}</dd>
                <dt>Finished</dt>
                <dd>{s.finished_at ? dt(s.finished_at) : '–'}</dd>
              </dl>
            </div>
          )
        })}
      </div>
    </section>
  )
}
